import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';
import { getDateFormat } from '../../utils/common-methods';
import LoadingSpinner from '../../components/LoadingSpinner';
import { NoContent, ReactTable } from '../../components/shared';
import { Box } from '@mui/material';
import useStyles from './style';
import NayaxAccordion from './NayaxAccordion';
import { Download } from '@mui/icons-material';
import dayjs from 'dayjs';
import { COLUMNS, NAYAX_TRACKINGS_TABLE } from '../../constants/tableColumns';
import { useGetNayaxTrackingsQuery } from '../../services/Nayax/NayaxService';
import { setNayaxPagination } from '../../redux-slice/nayaxSlice';

const NayaxTrackingsTable = ({ filterParams = {}, searchQuery = '', refreshTrigger = 0 }) => {
  const styles = useStyles();
  const dispatch = useDispatch();

  const { currentPage, perPageNumber } = useSelector(
    (state) =>
      state.nayax || {
        currentPage: DEFAULT_PAGE,
        perPageNumber: DEFAULT_PER_PAGE,
      },
  );

  const filterKey = useMemo(() => JSON.stringify(filterParams ?? {}), [filterParams]);

  const lastPerPageRef = useRef(perPageNumber);

  const queryParams = useMemo(() => {
    const params = {
      page: currentPage,
      perPage: perPageNumber,
      searchQuery,
      ...filterParams,
    };
    return params;
  }, [currentPage, perPageNumber, searchQuery, filterKey]);

  const {
    data: apiResponse,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetNayaxTrackingsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (refreshTrigger > 0) {
      refetch();
    }
  }, [refreshTrigger, refetch]);

  useEffect(() => {
    dispatch(
      setNayaxPagination({
        currentPage: DEFAULT_PAGE,
        perPageNumber,
      }),
    );
  }, [searchQuery, filterKey, dispatch, perPageNumber]);

  const columns = useMemo(
    () => [
      COLUMNS.EXPAND_ICON_COLUMN,
      ...NAYAX_TRACKINGS_TABLE(),
      {
        Header: () => 'Action',
        id: 'action',
        width: 80,
        isPadding: true,
        padding: '20px 32px 20px 0px',
        Cell: ({ row }) => {
          const { original } = row;
          return (
            <span
              data-testid="downloadIcon"
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: original.s3_file_url ? 'pointer' : 'not-allowed',
                opacity: original.s3_file_url ? 1 : 0.5,
              }}
            >
              {original.s3_file_url ? (
                <a
                  href={original.s3_file_url}
                  download={original.s3_file_name}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', color: 'inherit' }}
                >
                  <Download />
                </a>
              ) : (
                <Download />
              )}
            </span>
          );
        },
      },
    ],
    [],
  );

  const pageChangeHandler = (newPage1Based) => {
    dispatch(
      setNayaxPagination({
        currentPage: newPage1Based,
        perPageNumber,
      }),
    );
  };

  const perPageChangeHandler = (newPerPage) => {
    if (Number(newPerPage) === Number(lastPerPageRef.current)) {
      return;
    }

    lastPerPageRef.current = newPerPage;

    dispatch(
      setNayaxPagination({
        currentPage: DEFAULT_PAGE,
        perPageNumber: newPerPage,
      }),
    );
  };

  const detailLocationComponent = ({ row }) => {
    const original = row?.original;
    return original && <NayaxAccordion nayaxInfo={original} />;
  };

  const tableData = useMemo(() => {
    if (!apiResponse?.data?.nayaxTracking) {
      return [];
    }

    const nayaxData = apiResponse.data.nayaxTracking;

    if (!Array.isArray(nayaxData)) {
      return [];
    }

    return nayaxData.map((nayax) => ({
      ...nayax,
      job_id: nayax?.job_id || 'N/A',
      status: nayax?.status || 'Pending',
      created_at_formatted: nayax?.created_at ? getDateFormat(nayax?.created_at) : 'N/A',
      queue_start_time_formatted: nayax?.queue_start_time
        ? dayjs(nayax?.queue_start_time).format('MM/DD/YYYY HH:mm:ss')
        : 'N/A',
      queue_finish_time_formatted: nayax?.queue_finish_time
        ? dayjs(nayax?.queue_finish_time).format('MM/DD/YYYY HH:mm:ss')
        : 'N/A',
    }));
  }, [apiResponse]);

  const totalCount = apiResponse?.pagination?.total || apiResponse?.total || 0;

  if (isLoading || isFetching) {
    return (
      <Box className={styles.intercardTable}>
        <LoadingSpinner containerHeight={300} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={styles.intercardTable}>
        <NoContent title="No Nayax Data Found" desc="" />
      </Box>
    );
  }

  if (tableData.length === 0) {
    return (
      <Box className={styles.intercardTable}>
        <NoContent title="No Nayax Data Found" desc="" />
      </Box>
    );
  }

  return (
    <Box className={styles.intercardTable}>
      <ReactTable
        data={tableData}
        columns={columns}
        pagination
        manualPagination
        totalCount={totalCount}
        pageNumberForTablePagination={currentPage - 1}
        onPageChange={pageChangeHandler}
        onPerPageChange={perPageChangeHandler}
        perPage={perPageNumber}
        expandable
        renderRows
        renderRowSubComponent={detailLocationComponent}
      />
    </Box>
  );
};

export default NayaxTrackingsTable;
